// file: client/src/components/ui/index.jsx
/**
 * PHARMAKON — Shared UI Component Library  ·  ERP Design System v3
 * ─────────────────────────────────────────────────────────────────────────────
 * MIGRATED to v3: every component is now SELF-SUFFICIENT — it styles itself from
 * the global v3 tokens (var(--token), declared in index.css) and the erp
 * component classes (styles/global.css), so it renders correctly on ANY page,
 * including legacy pages that have dropped their <style>{BASE_CSS}</style> block.
 *
 * Theme: colours follow the global [data-theme] (useThemeStore) — there is no
 * per-component dark/light branching. The `c` prop is kept in every signature as
 * a HARMLESS NO-OP so existing call sites passing `c={c}` keep compiling; it is
 * no longer read. New code should omit it.
 *
 * Exports (unchanged surface): Ico, Bdg, Btn?, Toast, Spinner, Modal, EmptyState,
 *   PageHeader, DarkToggle, SearchInput, Pagination, FormGroup, Card, StatTile
 */

import { ICONS } from "../../config/theme.js";

// ── Icon ──────────────────────────────────────────────────────────────────────
export function Ico({ n, s = 14, style }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={s}
      height={s}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}
    >
      <path d={ICONS[n] || ICONS.chart} />
    </svg>
  );
}

// ── Badge ───────────────────────────────────────────────────────────────────
// Legacy tone letters → erp .bdg tones. teal (t) maps to blue/info per the v3
// CHIFA decision (v3 has no teal token).
const BDG_TONE = {
  g: "green",
  green: "green",
  r: "red",
  red: "red",
  a: "amber",
  amber: "amber",
  b: "blue",
  blue: "blue",
  t: "blue",
  teal: "blue",
  p: "purple",
  purple: "purple",
  gr: "gray",
  gray: "gray",
};
export function Bdg({ v, t = "g" }) {
  return <span className={`bdg ${BDG_TONE[t] || "gray"}`}>{v}</span>;
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ s = 18, color }) {
  return (
    <svg
      style={{
        animation: "spin .8s linear infinite",
        color: color || "currentColor",
      }}
      viewBox="0 0 24 24"
      width={s}
      height={s}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d={ICONS.spin_ico} />
    </svg>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
export function Toast({ toast }) {
  if (!toast) return null;
  const isErr = toast.type === "error";
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 18px",
        borderRadius: "var(--radius-lg)",
        background: isErr ? "var(--danger-soft)" : "var(--surf)",
        border: `1px solid ${isErr ? "var(--danger)" : "var(--border-default)"}`,
        boxShadow: "var(--shadow-panel)",
        fontSize: 12.5,
        fontWeight: 600,
        color: isErr ? "var(--danger)" : "var(--text-0)",
        maxWidth: 380,
        animation: "toast-in 180ms ease",
      }}
    >
      <Ico n={isErr ? "bell" : "check"} s={15} />
      {toast.msg}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 480,
}) {
  if (!open) return null;
  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--scrim)",
        zIndex: 90,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        className="modal-panel"
        style={{
          background: "var(--surf)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-modal)",
          width: "100%",
          maxWidth,
          boxShadow: "var(--shadow-overlay)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Head */}
        <div
          style={{
            padding: "15px 20px",
            borderBottom: "1px solid var(--border-subtle)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{ fontSize: 16, fontWeight: 700, color: "var(--text-0)" }}
          >
            {title}
          </span>
          <button
            className="xbtn"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "1px solid transparent",
              borderRadius: "var(--radius-sm)",
              padding: "4px 6px",
              cursor: "pointer",
              display: "flex",
              color: "var(--text-2)",
            }}
          >
            <Ico n="x" s={14} />
          </button>
        </div>
        {/* Body */}
        <div style={{ padding: 20 }}>{children}</div>
        {/* Footer */}
        {footer && (
          <div
            style={{
              padding: "12px 20px",
              borderTop: "1px solid var(--border-subtle)",
              background: "var(--surf-up)",
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({
  icon = "search",
  title = "Aucun résultat",
  sub,
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 24px",
        gap: 10,
        color: "var(--text-3)",
      }}
    >
      <Ico n={icon} s={36} style={{ opacity: 0.45 }} />
      <div
        style={{ fontSize: 13, fontWeight: 600, color: "var(--text-2)" }}
      >
        {title}
      </div>
      {sub && <div style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

// ── PageHeader ────────────────────────────────────────────────────────────────
export function PageHeader({ title, sub, actions }) {
  return (
    <header
      style={{
        height: 52,
        display: "flex",
        alignItems: "center",
        padding: "0 22px",
        gap: 12,
        background: "var(--surf)",
        borderBottom: "1px solid var(--border-subtle)",
        flexShrink: 0,
      }}
    >
      <div>
        <div
          style={{ fontSize: 15, fontWeight: 700, color: "var(--text-0)" }}
        >
          {title}
        </div>
        {sub && (
          <div
            style={{
              fontSize: 10,
              color: "var(--text-2)",
              marginTop: 1,
              letterSpacing: ".2px",
            }}
          >
            {sub}
          </div>
        )}
      </div>
      {actions && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginLeft: "auto",
          }}
        >
          {actions}
        </div>
      )}
    </header>
  );
}

// ── DarkToggle ────────────────────────────────────────────────────────────────
// Retained for source compatibility. The v3 migration unifies theming on the
// global switch (TweaksPanel), so new pages should not render this; kept styled
// from tokens for any remaining call site.
export function DarkToggle({ dark, onToggle }) {
  return (
    <button
      className="btn ghost icon sm"
      onClick={onToggle}
      title={dark ? "Mode clair" : "Mode sombre"}
      style={{ fontSize: 14 }}
    >
      {dark ? "☀" : "🌙"}
    </button>
  );
}

// ── SearchInput ───────────────────────────────────────────────────────────────
export function SearchInput({
  value,
  onChange,
  placeholder = "Rechercher…",
  loading = false,
  style,
}) {
  return (
    <div style={{ position: "relative", ...style }}>
      <span
        style={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--text-2)",
          pointerEvents: "none",
          display: "flex",
        }}
      >
        <Ico n="search" s={14} />
      </span>
      <input
        className="input"
        style={{ paddingLeft: 32, width: "100%" }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {loading && (
        <span
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <Spinner s={14} color="var(--text-2)" />
        </span>
      )}
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
export function Pagination({ page, total, limit, onPage }) {
  const totalPages = Math.ceil(total / limit) || 1;
  if (totalPages <= 1) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 16px",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <button
        className="btn secondary sm"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        <Ico n="chev_rt" s={10} style={{ transform: "rotate(180deg)" }} />
        Préc.
      </button>
      <span
        style={{
          fontSize: 11,
          color: "var(--text-2)",
          flex: 1,
          textAlign: "center",
        }}
      >
        Page {page} / {totalPages} · {total} entrée{total !== 1 ? "s" : ""}
      </span>
      <button
        className="btn secondary sm"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      >
        Suiv.
        <Ico n="chev_rt" s={10} />
      </button>
    </div>
  );
}

// ── FormGroup ─────────────────────────────────────────────────────────────────
export function FormGroup({ label, required, children, error }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 5,
        marginBottom: 12,
      }}
    >
      <label
        style={{
          fontSize: 9.5,
          fontWeight: 700,
          color: "var(--text-2)",
          textTransform: "uppercase",
          letterSpacing: ".7px",
        }}
      >
        {label}
        {required && (
          <span style={{ color: "var(--accent)", marginLeft: 2 }}>*</span>
        )}
      </label>
      {children}
      {error && (
        <span
          style={{
            fontSize: 10,
            color: "var(--danger)",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Ico n="bell" s={10} />
          {error}
        </span>
      )}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, style }) {
  return (
    <div className="panel" style={style}>
      {children}
    </div>
  );
}

// ── StatTile ──────────────────────────────────────────────────────────────────
// Re-anatomised onto the erp KPI instrument so it is self-sufficient (no longer
// depends on page-local .tile CSS). Same props + click/active behaviour.
export function StatTile({
  label,
  value,
  color,
  trend,
  trendDir,
  onClick,
  active,
}) {
  return (
    <div
      className="kpi pk-fade-in"
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        borderTopColor: active ? "var(--accent)" : undefined,
      }}
    >
      <div className="kl">{label}</div>
      <div className="kv" style={{ color: color || "var(--text-0)" }}>
        {value}
      </div>
      {trend !== undefined && (
        <div
          className="ks"
          style={{
            color:
              trendDir === "up"
                ? "var(--success)"
                : trendDir === "dn"
                  ? "var(--danger)"
                  : "var(--text-2)",
          }}
        >
          {trendDir === "up" ? "↑" : trendDir === "dn" ? "↓" : "—"} {trend}
        </div>
      )}
    </div>
  );
}
